import Papa from 'papaparse';
import { parse as parseSDF } from 'sdf-parser';

import { UserActions } from 'BMapsCmds';
import { App } from 'BMapsSrc/BMapsApp';
import { FoldingDataImportCase, PropApplicability } from 'BMapsSrc/model';
import { updateLocalJobState } from 'BMapsRedux/jobs/access';
import {
    Directionality, preparePropertyGroupMetadata, ScaleTypes,
} from 'BMapsSrc/integrations/properties_common';
import { ensureArray } from 'BMapsSrc/util/js_utils';
import { makeOneSDF } from 'BMapsSrc/util/mol_format_utils';
import { makeJobRequest } from './jobs_protocol';

/**
 * @typedef {'succeeded' | 'running' | 'failed' | 'starting' | 'unknown'} JobStatusOptions
 * @type {{
 *    SUCCEEDED: "succeeded",
 *    RUNNING: "running",
 *    FAILED: "failed",
 *    STARTING: "starting",
 *    UNKNOWN: "unknown"
 * }}
 */
export const JobStatus = {
    SUCCEEDED: 'succeeded',
    RUNNING: 'running',
    FAILED: 'failed',
    STARTING: 'starting',
    UNKNOWN: 'unknown',
};

/**
 * @typedef {'folding' | 'maplight' | 'gnina_scoring' | 'reinvent' |
 * 'gnina_docking' | 'library_screen' } JobTypeOptions
 * @type {{
 *    FOLDING: "folding",
 *    MAPLIGHT: "maplight",
 *    GNINA_SCORING: "gnina_scoring",
 *    REINVENT: "reinvent"
 *    GNINA_DOCKING: "gnina_docking",
 *    GNINA_FLEXIBLE_DOCKING: "gnina_flexible_docking",
 *    LIBRARY_SCREEN: "library_screen"
 * }}
 */
export const JobTypes = {
    FOLDING: 'folding',
    MAPLIGHT: 'maplight',
    GNINA_SCORING: 'gnina_scoring',
    REINVENT: 'reinvent',
    GNINA_DOCKING: 'gnina_docking',
    GNINA_FLEXIBLE_DOCKING: 'gnina_flexible_docking',
    LIBRARY_SCREEN: 'library_screen',
};

export const jobDefinitions = {
    [JobTypes.FOLDING]: {
        jobTypeId: JobTypes.FOLDING,
        isLongRunning: true,
        loadsMacromoleculeStructure: true,
        getJobTypeName() { return 'Folding'; },
        getJobName(job) { return 'Folding Job'; },
        prepareRequest(jobrequest, stash={}) {
            const requestmeta = {
                moleculeName: stash?.mapCaseParams?.name || undefined,
                foldingProgram: jobrequest.foldingProgramId,
            };
            const requestData = makeJobRequest({
                jobtype: this.jobTypeId, jobrequest, stash, requestmeta,
            });
            return requestData;
        },
        getRequestColumns() { },
        getResponseColumns() { },
        async integrateResults(job, results, options) {
            let resultsPacket = results;
            let error;
            try {
                if (typeof resultsPacket === 'string') resultsPacket = JSON.parse(resultsPacket);
                if (!resultsPacket) {
                    return { error: 'Empty results' };
                }
                let jobResults = resultsPacket.jobResults;
                if (typeof jobResults === 'string') {
                    // For now, we expect that the results are already an object.
                    // But parsing here let's us load job results from before this change.
                    jobResults = JSON.parse(jobResults);
                }
                const firstResult = jobResults?.find((r) => r.data);
                if (!firstResult) {
                    return { error: 'Empty results' };
                }
                const stash = resultsPacket?.stash;
                const loadOptions = { ...stash?.loadOptions, ...options?.loadOptions };
                const mapCase = new FoldingDataImportCase({
                    data: firstResult.data,
                    format: firstResult.format,
                    foldingData: {
                        foldingProgramId: resultsPacket.jobrequest.foldingProgramId,
                        foldingArgs: resultsPacket.jobrequest,
                    },
                    resultProperties: firstResult.resultProperties,
                    loadOptions,
                    ...stash?.mapCaseParams,
                });
                const { errors } = await UserActions.ChooseProtein(
                    mapCase, loadOptions, options?.dataConnection
                );
                if (errors.length > 0) {
                    error = `Failed to load the folding results into BMaps: ${errors.join('; ')}`;
                }
            } catch (ex) {
                error = 'Error loading folding results';
                console.error(error, ex);
            }
            return { error, results: resultsPacket };
        },
    },
    [JobTypes.MAPLIGHT]: {
        jobTypeId: JobTypes.MAPLIGHT,
        loadsMacromoleculeStructure: false,
        getJobTypeName() { return 'MapLight'; },
        getJobName(job) { return 'MapLight Job'; },
        prepareRequest(jobrequest, stash={}) {
            const requestData = makeJobRequest({
                jobtype: this.jobTypeId, jobrequest, stash, requestmeta: {},
            });
            return requestData;
        },
        async integrateResults(job, results, options) {
            let resultsPacket = results;
            let error;
            try {
                if (!resultsPacket) return { error: 'Empty results' };
                if (typeof resultsPacket === 'string') resultsPacket = JSON.parse(resultsPacket);
                if (!resultsPacket.jobResults) return { error: 'Empty results' };
                const { recalculate } = resultsPacket.stash || {};
                const allCmpds = App.Workspace.getLoadedCompounds();
                for (const [structId, maplightResult] of Object.entries(resultsPacket.jobResults)) {
                    const compounds = allCmpds.filter((c) => c.getUniqueStructId() === structId);
                    const scopeName = JobTypes.MAPLIGHT;
                    for (const c of compounds) {
                        for (const [propName, value] of Object.entries(maplightResult)) {
                            if (recalculate || c.getProperty(scopeName, propName) === undefined) {
                                if (value !== undefined) {
                                    c.setProperty(scopeName, propName, value);
                                }
                            }
                        }
                    }
                }
            } catch (ex) {
                error = 'Error loading MapLight results';
                console.error(error, ex);
            }

            return { error, results: resultsPacket };
        },
    },
    [JobTypes.GNINA_SCORING]: {
        jobTypeId: JobTypes.GNINA_SCORING,
        loadsMacromoleculeStructure: false,
        getJobTypeName() { return 'Gnina Scoring'; },
        getJobName(job) { return 'Gnina Scoring Job'; },
        prepareRequest(jobrequest, stash={}) {
            const requestData = makeJobRequest({
                jobtype: this.jobTypeId, jobrequest, stash, requestmeta: {},
            });
            return requestData;
        },
        async integrateResults(job, results, options) {
            let resultsPacket = results;
            let error;
            try {
                if (!resultsPacket) return { error: 'Empty results' };
                if (typeof resultsPacket === 'string') resultsPacket = JSON.parse(resultsPacket);
                if (!resultsPacket.jobResults) return { error: 'Empty results' };
                // Properties extracted later
            } catch (ex) {
                error = `Error loading ${this.jobTypeId} results`;
                console.error(error, ex);
            }

            return { error, results: resultsPacket };
        },
    },
    [JobTypes.REINVENT]: {
        jobTypeId: JobTypes.REINVENT,
        isLongRunning: true,
        loadsMacromoleculeStructure: false,
        loadsCompound: true,
        getJobTypeName() { return 'REINVENT 4'; },
        getJobName(job) { return 'Reinvent Job'; },
        prepareRequest(jobrequest, stash={}) {
            const requestData = makeJobRequest({
                jobtype: this.jobTypeId,
                jobrequest,
                stash,
                requestmeta: {
                    jobName: jobrequest.jobName,
                },
            });
            return requestData;
        },
        async integrateResults(job, results) {
            let structures = null;
            if (results.jobResults?.results) {
                structures = Papa.parse(
                    results.jobResults.results, { header: true, dynamicTyping: true }
                ).data;
            }
            updateLocalJobState(job.jobid, { structures });
            return { results };
        },
        idField: 'SMILES',
        dataInfo: {
            SMILES: {
                label: 'Compound Structure',
                structureValue: true,
                defaultVisible: true,
            },
            Score: {
                label: 'Score',
                description: 'Composite multi-parameter optimization score after combining all objectives and transforms.',
                unit: 'Score (0-1)',
                directionality: Directionality.ONEHUNDRED,
                range: '0.0 - 1.0',
                goodRange: '>0.7',
                defaultVisible: true,
            },
            Agent: {
                label: 'Agent',
                description: 'Model (policy) log-likelihood of generating the SMILES under the current agent (higher/less negative is better).',
                unit: 'log-likelihood (nats)',
                directionality: Directionality.MAXIMIZE,
                range: 'Unbounded (typically -50 to 0)',
                goodRange: '>-10',
                defaultVisible: true,
            },
            Prior: {
                label: 'Prior',
                description: 'Reference model (prior) log-likelihood of the SMILES; used to regularize against distributional drift.',
                unit: 'log-likelihood (nats)',
                directionality: Directionality.MAXIMIZE,
                range: 'Unbounded (typically -50 to 0)',
                goodRange: '>-10',
                defaultVisible: true,
            },
            Target: {
                label: 'Target',
                description: 'Combined objective score before final scaling (depends on your scoring function setup).',
                unit: 'Score (0-1)',
                directionality: Directionality.ONEHUNDRED,
                range: '0.0 - 1.0',
                goodRange: '>0.7',
                defaultVisible: true,
            },
            'Molecular weight': {
                label: 'Weighted Molecular Weight',
                description: 'Sum of atomic masses for the molecule.',
                unit: 'Da',
                directionality: Directionality.NOT_APPLICABLE,
                range: '50 - 1000',
                goodRange: '200 - 500',
                excludeFromTable: true,
            },
            'Number of HB acceptors (Lipinski)': {
                label: 'Weighted H-Bond Acceptors (Lipinski)',
                description: 'Count of hydrogen bond acceptors as defined by Lipinski.',
                unit: 'count',
                directionality: Directionality.NOT_APPLICABLE,
                range: '0 - 20',
                goodRange: '≤10',
            },
            'Number of HB donors (Lipinski)': {
                label: 'Weighted H-Bond Donors (Lipinski)',
                description: 'Count of hydrogen bond donors as defined by Lipinski.',
                unit: 'count',
                directionality: Directionality.NOT_APPLICABLE,
                range: '0 - 10',
                goodRange: '≤5',
            },
            'QED Score': {
                label: 'Weighted QED Score',
                description: 'Quantitative estimate of drug-likeness combining multiple physicochemical properties.',
                unit: 'Score (0-1)',
                directionality: Directionality.ONEHUNDRED,
                range: '0.0 - 1.0',
                goodRange: '>0.6',
                excludeFromTable: true,
            },
            'SA score': {
                label: 'Weighted Synthetic Accessibility',
                description: 'Heuristic estimate of ease of synthesis; lower values indicate easier synthesis.',
                unit: 'SA (1-10)',
                directionality: Directionality.MINIMIZE,
                range: '1 - 10',
                goodRange: '<6',
                excludeFromTable: true,
            },
            TPSA: {
                label: 'Weighted TPSA',
                description: 'Sum of surfaces of polar atoms; correlates with absorption, BBB penetration, and permeability.',
                unit: 'Å²',
                directionality: Directionality.NOT_APPLICABLE,
                range: '0 - 250',
                goodRange: '20 - 130',
            },
            'SlogP (RDKit)': {
                label: 'Weighted LogP (RDKit)',
                description: 'Octanol/water partition coefficient estimate (lipophilicity).',
                unit: 'logP',
                directionality: Directionality.NOT_APPLICABLE,
                range: '-2 - 8',
                goodRange: '1 - 3',
            },
            'Molecular weight (raw)': {
                label: 'Molecular Weight',
                description: 'Unnormalized molecular weight prior to any scaling or transforms.',
                unit: 'Da',
                directionality: Directionality.NOT_APPLICABLE,
                range: '50 - 1000',
                goodRange: '200 - 500',
                defaultVisible: true,
            },
            'Number of HB acceptors (Lipinski) (raw)': {
                label: 'H-Bond Acceptors (Lipinski)',
                description: 'Unnormalized acceptor count.',
                unit: 'count',
                directionality: Directionality.NOT_APPLICABLE,
                range: '0 - 20',
                goodRange: '≤10',
            },
            'Number of HB donors (Lipinski) (raw)': {
                label: 'H-Bond Donors (Lipinski)',
                description: 'Unnormalized donor count.',
                unit: 'count',
                directionality: Directionality.NOT_APPLICABLE,
                range: '0 - 10',
                goodRange: '≤5',
            },
            'SA score (raw)': {
                label: 'Synthetic Accessibility',
                description: 'Unnormalized Ertl SA score; lower is easier to synthesize.',
                unit: 'SA (1-10)',
                directionality: Directionality.MINIMIZE,
                range: '1 - 10',
                goodRange: '<6',
                defaultVisible: true,
            },
            'QED Score (raw)': {
                label: 'QED Score',
                description: 'Unnormalized QED value prior to final scaling.',
                unit: 'Score (0-1)',
                directionality: Directionality.ONEHUNDRED,
                range: '0.0 - 1.0',
                goodRange: '>0.6',
                defaultVisible: true,
            },
            'SlogP (RDKit) (raw)': {
                label: 'LogP (RDKit)',
                description: 'Unnormalized logP value prior to transforms.',
                unit: 'logP',
                directionality: Directionality.NOT_APPLICABLE,
                range: '-2 - 8',
                goodRange: '1 - 3',
                defaultVisible: true,
            },
            'TPSA (raw)': {
                label: 'TPSA',
                description: 'Unnormalized TPSA prior to transforms.',
                unit: 'Å²',
                directionality: Directionality.NOT_APPLICABLE,
                range: '0 - 250',
                goodRange: '20 - 130',
                defaultVisible: true,
            },
            // Uncomment the following to completely exclude these columns from the table altogether
            // ...['SMILES_state', 'Input_SMILES', 'Scaffold', 'step'].reduce((acc, propName) => ({
            //     ...acc, [propName]: { excludeFromTable: true },
            // }), {}),
            // In lieu of fully specifying the rest of the RDKit properties,
            // just make sure we're only including one value instead of both regular and raw.
            ...[
                'GraphLength', 'NumAtomStereoCenters', 'NumRotBond',
                'Csp3', 'Numsp', 'Numsp2', 'Numsp3',
                'NumHeavyAtoms', 'NumHeteroAtoms',
                'NumRings', 'NumAromaticRings', 'NumAliphaticRings',
            ].reduce((acc, propName) => {
                const regularTag = `${propName} (RDKit)`;
                const rawTag = `${regularTag} (raw)`;
                // These properties have the same values for regular and raw,
                // so only include raw (value before transform for scoring)
                acc[regularTag] = {
                    label: regularTag,
                    excludeFromTable: true,
                };
                acc[rawTag] = {
                    label: regularTag,
                };
                return acc;
            }, {}),
        },
    },
    [JobTypes.GNINA_DOCKING]: {
        jobTypeId: JobTypes.GNINA_DOCKING,
        loadsMacromoleculeStructure: false,
        loadsCompound: true,
        getJobTypeName() { return 'Gnina Docking'; },
        getJobName(job) { return 'Gnina Docking Job'; },
        prepareRequest(jobrequest, stash={}) {
            const requestData = makeJobRequest({
                jobtype: this.jobTypeId, jobrequest, stash, requestmeta: {},
            });
            return requestData;
        },
        async integrateResults(job, results, options) {
            let resultsPacket = results;
            let error;
            let poses = [];
            try {
                if (!resultsPacket) return { error: 'Empty results' };
                if (typeof resultsPacket === 'string') resultsPacket = JSON.parse(resultsPacket);
                if (!resultsPacket.jobResults) return { error: 'Empty results' };
                const cmpdSmiles = resultsPacket.stash?.cmpdSmiles || {};
                const parsed = parseSDF(resultsPacket.jobResults.sdf);
                poses = parsed.molecules.map((entry, i) => {
                    const sourceSpec = entry.bmapsResSpec;
                    const smiles = cmpdSmiles[sourceSpec]?.smiles || null;
                    const sdf = makeOneSDF(entry.molfile, { tmpSpec: sourceSpec, tmpI: i });
                    return {
                        id: `${job.jobid}_${i}`,
                        sourceSpec,
                        SMILES: smiles,
                        sdf,
                        affinity: entry.minimizedAffinity,
                        cnn_score: entry.CNNscore,
                        cnn_affinity: entry.CNNaffinity,
                        cnn_vs: entry.CNN_VS,
                        cnn_variance: entry.CNNaffinity_variance,
                    };
                });
                updateLocalJobState(job.jobid, { structures: poses });
            } catch (ex) {
                error = `Error loading ${this.jobTypeId} results`;
                console.error(error, ex);
            }
            return { error, results: { poses, jobResults: resultsPacket.jobResults } };
        },
        propertyScope: 'gnina_scoring',
        dataInfo: {
            SMILES: {
                label: 'Structure',
                structureValue: true,
                defaultVisible: true,
            },
            sourceSpec: {
                label: 'Source Cmpd',
                defaultVisible: true,
            },
            // Copied from gninaProps in gnina.js
            affinity: {
                label: 'Affinity (AutoDock Score)',
                description: 'Predicted binding affinity from AutoDock Vina scoring function',
                unit: 'kcal/mol',
                directionality: Directionality.MINIMIZE,
                defaultVisible: true,
            },
            cnn_affinity: {
                label: 'Gnina Affinity (CNN)',
                description: "Gnina's prediction of binding affinity in pK units (6 = 1 µM, 9 = 1 nM)",
                unit: 'pK',
                directionality: Directionality.MAXIMIZE,
                defaultVisible: true,
            },
            cnn_score: {
                label: 'Gnina Pose Score',
                description: "Gnina's predicted probability that the pose is within 2Å of the correct binding pose",
                unit: 'Probability',
                directionality: Directionality.ONEHUNDRED,
                defaultVisible: true,
            },
            cnn_vs: {
                label: 'Gnina VS',
                description: "Gnina's virtual screening score (Gnina affinity * Gnina pose score)",
                unit: '(composite)',
                directionality: Directionality.MAXIMIZE,
                defaultVisible: true,
            },
            cnn_variance: {
                label: 'Gnina CNN Variance',
                description: "Gnina's report of the variance/uncertainty of the CNN affinity scoring, a measure of confidence in the prediction",
                unit: 'pK Variance',
                directionality: Directionality.ZERO,
            },
        },
    },
    [JobTypes.LIBRARY_SCREEN]: {
        jobTypeId: JobTypes.LIBRARY_SCREEN,
        loadsMacromoleculeStructure: false,
        isLongRunning: true,
        loadsCompound: false,
        getJobTypeName() { return 'Library Screening'; },
        getJobName(job) { return 'Library Screening Job'; },
        prepareRequest(jobrequest, stash={}) {
            const requestData = makeJobRequest({
                jobtype: this.jobTypeId,
                jobrequest,
                stash,
                requestmeta: {
                    jobName: jobrequest.jobName,
                },
            });
            return requestData;
        },
        /**
         *
         * @param {{ jobid: string }} job
         * @param {{ jobResults: [{ cmpd_id: string, smiles: string, csv: string }] }} results
         * @param {{}} options
         * @returns
         */
        async integrateResults(job, results, options) {
            let compounds = results?.jobResults || [];
            if (typeof compounds === 'string') {
                compounds = JSON.parse(compounds);
            }
            function deriveProps(properties) {
                const derivedProps = {};
                function reserveColumns(propNames) {
                    // Assign null values to derived properties to ensure they show up in order
                    ensureArray(propNames).forEach((pn) => { derivedProps[pn] = null; });
                }
                // Derived from Boltz IC50 values
                for (const [key, value] of Object.entries(properties)) {
                    if (key.startsWith('boltz_affinity__affinity_derived_ic50')) {
                        const derivedKey = `${key}_nM`;
                        reserveColumns(derivedKey);
                        if (value != null) derivedProps[derivedKey] = value * 1000;
                    }
                }
                // Derived from energy values
                reserveColumns(['bfd__interaction_total', 'bfd__amber_total', 'bfd__energy_sum']);
                const vdW = properties['bfd__vdw'];
                const electrostatics = properties['bfd__electrostatics'];
                const stress = properties['bfd__stress'];
                const ddGs = properties['bfd__ddGs'];
                if (vdW != null || electrostatics != null) {
                    const interactionTotal = (vdW ?? 0) + (electrostatics ?? 0);
                    derivedProps['bfd__interaction_total'] = interactionTotal;
                    if (stress != null) {
                        const amberTotal = interactionTotal + stress;
                        derivedProps['bfd__amber_total'] = amberTotal;
                        if (ddGs != null) {
                            derivedProps['bfd__energy_sum'] = amberTotal + ddGs;
                        }
                    }
                }
                return derivedProps;
            }

            const res = []; // will be added to localJobState
            for (const { cmpd_id: name, smiles, csv } of compounds) {
                if (csv) {
                    // CSV is per compound, properties as columns and library items as rows.
                    const allTargetsProps = Papa.parse(
                        csv, { header: true, dynamicTyping: true, skipEmptyLines: true }
                    ).data;
                    const resultsData = {
                        structure: smiles,
                        name,
                    };
                    let result = res.find((r) => r.structure === resultsData.structure);
                    for (const row of allTargetsProps) {
                        for (const [propName, value] of Object.entries(row)) {
                            if (propName === 'library_item_id') continue;
                            // Doing result assignment here inside the property loop means that
                            // we won't add a compound entry without any properties.
                            if (!result) {
                                result = {
                                    structure: resultsData.structure,
                                    name: resultsData.name,
                                    properties: {},
                                };
                                res.push(result);
                            }
                            const prevEntry = result.properties[row.library_item_id];
                            result.properties[row.library_item_id] = {
                                ...prevEntry,
                                [propName]: value,
                            };
                        }
                        if (result) {
                            // There won't be a result if there were no properties
                            Object.assign(result.properties[row.library_item_id], deriveProps(row));
                        }
                    }
                }
            }
            updateLocalJobState(job.jobid, { results: res });
            return { results };
        },
        // TODO for dataInfo:
        // - Review text
        // - Additional boltz confidence properties need descriptions
        // - min/max considerations (not currently passed in since Boltz only gives 1 conformation)
        dataInfo: {
            boltz_affinity__affinity_pred_value: {
                label: 'Affinity (Boltz-2)',
                description: "Boltz-2's predicted affinity of an active ligand to the target, from the ensemble model. Suggested for hit-to-lead or lead-optimization.",
                unit: 'log(IC50) μM',
                directionality: Directionality.MINIMIZE,
                defaultVisible: true,
                defaultThresholds: { max: -1 }, // Match IC50 μM < 0.1
            },
            boltz_affinity__affinity_probability_binary: {
                label: 'Binding Probability (Boltz-2)',
                description: "Boltz-2's predicted probability that a ligand binds to the target, from the ensemble model. Suggested for hit identification.",
                unit: 'Probability',
                directionality: Directionality.ONEHUNDRED,
                defaultVisible: true,
                defaultThresholds: { min: 0.50 },
                defaultActive: true,
            },
            boltz_affinity__affinity_pred_value1: {
                label: 'Affinity (Model #1) (Boltz-2)',
                description: "Boltz-2's predicted affinity of an active ligand to the target, from the first model in the ensemble.",
                unit: 'log(IC50) μM',
                directionality: Directionality.MINIMIZE,
                defaultThresholds: { max: -1 },
                advanced: true,
            },
            boltz_affinity__affinity_probability_binary1: {
                label: 'Binding Probability (Model #1) (Boltz-2)',
                description: "Boltz-2's predicted probability that a ligand binds to the target, from the first model in the ensemble.",
                unit: 'Probability',
                directionality: Directionality.ONEHUNDRED,
                defaultThresholds: { min: 0.50 },
                advanced: true,
            },
            boltz_affinity__affinity_pred_value2: {
                label: 'Affinity (Model #2) (Boltz-2)',
                description: "Boltz-2's predicted affinity of an active ligand to the target, from the second model in the ensemble.",
                unit: 'log(IC50) μM',
                directionality: Directionality.MINIMIZE,
                defaultThresholds: { max: -1 },
                advanced: true,
            },
            boltz_affinity__affinity_probability_binary2: {
                label: 'Binding Probability (Model #2) (Boltz-2)',
                description: "Boltz-2's predicted probability that a ligand binds to the target, from the second model in the ensemble.",
                unit: 'Probability',
                directionality: Directionality.ONEHUNDRED,
                defaultThresholds: { min: 0.50 },
                advanced: true,
            },
            boltz_affinity__affinity_derived_ic50: {
                label: 'IC50 μM (Boltz-2)',
                description: "Predicted IC50 based on Boltz-2's affinity prediction",
                unit: 'μM',
                precision: 3,
                directionality: Directionality.MINIMIZE,
                defaultScale: ScaleTypes.LOGARITHMIC,
                defaultThresholds: { max: 0.1 },
            },
            boltz_affinity__affinity_derived_ic501: {
                label: 'IC50 μM (Model #1) (Boltz-2)',
                description: "Predicted IC50 based on Boltz-2's affinity prediction from the first model in the ensemble.",
                unit: 'μM',
                precision: 3,
                directionality: Directionality.MINIMIZE,
                defaultScale: ScaleTypes.LOGARITHMIC,
                defaultThresholds: { max: 0.1 },
                advanced: true,
            },
            boltz_affinity__affinity_derived_ic502: {
                label: 'IC50 μM (Model #2) (Boltz-2)',
                description: "Predicted IC50 based on Boltz-2's affinity prediction from the second model in the ensemble.",
                unit: 'μM',
                precision: 3,
                directionality: Directionality.MINIMIZE,
                defaultScale: ScaleTypes.LOGARITHMIC,
                defaultThresholds: { max: 0.1 },
                advanced: true,
            },
            boltz_affinity__affinity_derived_ic50_nM: {
                label: 'IC50 nM (Boltz-2)',
                description: "Predicted IC50 based on Boltz-2's affinity prediction",
                unit: 'nM',
                precision: 1,
                directionality: Directionality.MINIMIZE,
                defaultScale: ScaleTypes.LOGARITHMIC,
                defaultVisible: true,
                defaultThresholds: { max: 100 },
            },
            boltz_affinity__affinity_derived_ic501_nM: {
                label: 'IC50 nM (Model #1) (Boltz-2)',
                description: "Predicted IC50 based on Boltz-2's affinity prediction from the first model in the ensemble.",
                unit: 'nM',
                precision: 1,
                directionality: Directionality.MINIMIZE,
                defaultScale: ScaleTypes.LOGARITHMIC,
                defaultThresholds: { max: 100 },
                advanced: true,
            },
            boltz_affinity__affinity_derived_ic502_nM: {
                label: 'IC50 nM (Model #2) (Boltz-2)',
                description: "Predicted IC50 based on Boltz-2's affinity prediction from the second model in the ensemble.",
                unit: 'nM',
                precision: 1,
                directionality: Directionality.MINIMIZE,
                defaultScale: ScaleTypes.LOGARITHMIC,
                defaultThresholds: { max: 100 },
                advanced: true,
            },
            boltz_confidence__confidence_score: {
                label: 'Boltz-2 Confidence Score',
                unit: 'Probability',
                directionality: Directionality.ONEHUNDRED,
                defaultVisible: true,
            },
            boltz_confidence__ptm: {
                label: 'pTM (Boltz-2)',
                unit: 'Probability',
                directionality: Directionality.ONEHUNDRED,
                advanced: true,
            },
            boltz_confidence__iptm: {
                label: 'ipTM (Boltz-2)',
                unit: 'Probability',
                directionality: Directionality.ONEHUNDRED,
                advanced: true,
            },
            boltz_confidence__ligand_iptm: {
                label: 'Ligand ipTM (Boltz-2)',
                unit: 'Probability',
                directionality: Directionality.ONEHUNDRED,
                advanced: true,
            },
            boltz_confidence__protein_iptm: {
                label: 'Protein ipTM (Boltz-2)',
                unit: 'Probability',
                directionality: Directionality.ONEHUNDRED,
                advanced: true,
            },
            boltz_confidence__complex_plddt: {
                label: 'Complex pLDDT (Boltz-2)',
                unit: 'Probability',
                directionality: Directionality.ONEHUNDRED,
                advanced: true,
            },
            boltz_confidence__complex_iplddt: {
                label: 'Complex ipLDDT (Boltz-2)',
                unit: 'Probability',
                directionality: Directionality.ONEHUNDRED,
                advanced: true,
            },
            boltz_confidence__complex_pde: {
                label: 'Complex pDE (Boltz-2)',
                unit: 'Å',
                directionality: Directionality.MINIMIZE,
                advanced: true,
            },
            boltz_confidence__complex_ipde: {
                label: 'Complex ipDE (Boltz-2)',
                unit: 'Å',
                directionality: Directionality.MINIMIZE,
                advanced: true,
            },
            bfd__vdw: {
                label: 'vdW (Amber)',
                description: 'Calculated van der Waals interaction energy between ligand and receptor',
                unit: 'kcal/mol',
                directionality: Directionality.MINIMIZE,
                defaultThresholds: { max: 0 },
                advanced: true,
            },
            bfd__electrostatics: {
                label: 'Electrostatics (Amber)',
                description: 'Calculated electrostatic interaction energy between ligand and receptor',
                unit: 'kcal/mol',
                directionality: Directionality.MINIMIZE,
                advanced: true,
            },
            bfd__interaction_total: {
                label: 'Interaction Energy (Amber)',
                description: 'Sum of vdW and electrostatic energies between ligand and receptor',
                unit: 'kcal/mol',
                directionality: Directionality.MINIMIZE,
                defaultVisible: true,
                defaultThresholds: { max: 0 },
            },
            bfd__stress: {
                label: 'Stress Energy (Amber)',
                description: 'Calculated internal strain energy within the ligand',
                unit: 'kcal/mol',
                directionality: Directionality.MINIMIZE,
            },
            bfd__ddGs: {
                label: 'ΔΔGs (BMaps)',
                description: 'Predicted cost of solvation',
                unit: 'kcal/mol',
                directionality: Directionality.MINIMIZE,
            },
            bfd__amber_total: {
                label: 'Total Amber Energy',
                description: 'Sum of interaction and internal (stress) energies for the bound ligand pose. This value may be less predictive than the interaction energy.',
                unit: 'kcal/mol',
                directionality: Directionality.MINIMIZE,
                defaultThresholds: { max: 0 },
                advanced: true,
            },
            bfd__energy_sum: {
                label: 'BMaps Energy Total',
                description: 'Sum of interaction energy, internal (stress) energy, and desolvation cost (ΔΔGs) for the bound ligand pose. This value may be less predictive than the interaction energy.',
                unit: 'kcal/mol',
                directionality: Directionality.MINIMIZE,
                defaultThresholds: { max: 0 },
                advanced: true,
            },
            gnina__minimizedAffinity: {
                label: 'Affinity (AutoDock Score)',
                description: 'Predicted binding affinity from AutoDock Vina scoring function',
                unit: 'kcal/mol',
                directionality: Directionality.MINIMIZE,
                defaultVisible: true,
            },
            gnina__CNNaffinity: {
                label: 'Gnina Affinity (CNN)',
                description: "Gnina's prediction of binding affinity in pK units (6 = 1 µM, 9 = 1 nM)",
                unit: 'pK',
                directionality: Directionality.MAXIMIZE,
                defaultVisible: true,
                defaultThresholds: { min: 7 }, // match Boltz IC50 nM < 100
            },
            gnina__CNNscore: {
                label: 'Gnina Pose Score',
                description: "Gnina's predicted probability that the pose is within 2Å of the correct binding pose",
                unit: 'Probability',
                directionality: Directionality.ONEHUNDRED,
                defaultVisible: true,
            },
            gnina__CNN_VS: {
                label: 'Gnina VS',
                description: "Gnina's virtual screening score (Gnina affinity * Gnina pose score)",
                unit: '(composite)',
                directionality: Directionality.MAXIMIZE,
                defaultVisible: true,
            },
            gnina__CNNaffinity_variance: {
                label: 'Gnina Variance',
                description: "Gnina's report of the variance/uncertainty of the CNN affinity scoring, a measure of confidence in the prediction",
                unit: 'pK Variance',
                directionality: Directionality.ZERO,
                advanced: true,
            },
        },
    },
};

export function lookupJobDefinition(jobTypeId) {
    return jobDefinitions[jobTypeId];
}

export const getPropertyMetadata = () => ({
    [JobTypes.REINVENT]: preparePropertyGroupMetadata({
        groupMeta: {
            propApplicability: PropApplicability.PER_COMPOUND,
            label: 'Reinvent properties',
        },
        propertyInfo: jobDefinitions[JobTypes.REINVENT].dataInfo,
        tooltipHeading: 'Reinvent properties',
    }),
});
